import classnames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import chunk from 'lodash-es/chunk';
import { isNil } from 'lodash-es';
import { useSelector } from 'react-redux';
import { Flex, Segmented, Skeleton, Typography } from 'antd';
import { SitePerformanceChartJsChart } from './SitePerformanceChartJsChart';
import { StyledSitePerformanceChartJs, StyledSitePerformanceChartJsWrapper } from './SitePerformanceChartJs.styles';
import { SitePerformanceChartJsGlobalStyles } from './SitePerformanceChartJs.global.styles';
import { SITE_PERFORMANCE_CHART_PAGE_SIZE } from 'app/modules/site-management/widgets/fleet-dashboard/constants/SitePerformanceChart.constants';
import {
  SitePerformanceChartData,
  SitePerformanceChartDatum,
  SitePerformanceChartUtils,
} from 'app/modules/site-management/widgets/fleet-dashboard/utils/SitePerformanceChartUtils';
import * as authenticationSelectors from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { kccThemeConstants } from 'config/theme/kccTheme/kccTheme';
import { KccButton } from 'lib/components/KccButton/KccButton';
import { SvgIcon } from 'lib/components/SvgIcon/SvgIcon';
import { NoResultsEmpty } from 'lib/components/NoResultsEmpty/NoResultsEmpty';

type SortOrder = 'alphabetical' | 'ascending' | 'descending';

export interface SitePerformanceChartJsProps {
  data: SitePerformanceChartData[];
  startDate: string;
  endDate: string;
  isLoading: boolean;
}

export const SitePerformanceChartJs = ({
  data,
  startDate,
  endDate,
  isLoading,
}: SitePerformanceChartJsProps): JSX.Element => {
  const { t } = useTranslation();
  const [chartIndex, setChartIndex] = useState<number>(0);
  const [sortOrder, setSortOrder] = useState<SortOrder>('ascending');
  const isGcd = useSelector(authenticationSelectors.selectHasAccessToGCD);

  const isEmpty = data.length === 0 || data[0].data.length === 0;

  const sortedData = useMemo((): SitePerformanceChartData[] => {
    if (isEmpty) {
      return [];
    }
    const actualItems = [...data[0].data];
    const plannedItems = data[1]?.data ? [...data[1].data] : [];

    const paired = actualItems.map((actual, i) => ({
      actual,
      planned: plannedItems[i] as SitePerformanceChartDatum | undefined,
    }));

    if (sortOrder === 'alphabetical') {
      paired.sort((a, b) => a.actual.primary.localeCompare(b.actual.primary));
    } else if (sortOrder === 'ascending') {
      paired.sort((a, b) => a.actual.secondary - b.actual.secondary);
    } else {
      paired.sort((a, b) => b.actual.secondary - a.actual.secondary);
    }

    const sortedActual = paired.map(p => p.actual);
    const sortedPlanned = paired.map(p => p.planned).filter((p): p is SitePerformanceChartDatum => p !== undefined);

    return [
      { ...data[0], data: sortedActual },
      { ...data[1], data: sortedPlanned },
    ];
  }, [data, isEmpty, sortOrder]);

  const actualPages = useMemo(
    () => (sortedData.length > 0 ? chunk(sortedData[0].data, SITE_PERFORMANCE_CHART_PAGE_SIZE) : []),
    [sortedData]
  );
  const { actual: pageActualData, planned: pagePlannedData } = useMemo(
    () =>
      sortedData.length > 0
        ? SitePerformanceChartUtils.getPageChartData(sortedData, chartIndex, isGcd)
        : { actual: [], planned: [] },
    [sortedData, chartIndex, isGcd]
  );

  const shouldRenderChart = useMemo<boolean>(() => {
    if (isGcd) {
      return pageActualData.length > 0 && pagePlannedData.length > 0;
    }

    return pageActualData.length > 0;
  }, [isGcd, pageActualData, pagePlannedData]);

  const pageSiteCount = pageActualData.length;

  const chartPlotHeight = useMemo(
    (): number => SitePerformanceChartUtils.getContainerHeight(pageSiteCount, isGcd),
    [pageSiteCount, isGcd]
  );

  const isFirstPage = chartIndex === 0;
  const isLastPage = chartIndex >= actualPages.length - 1;
  const hasPagination = actualPages.length > 1;

  useEffect((): void => {
    setChartIndex(0);
  }, [data, sortOrder]);

  const onClickPrevBtn = (): void => setChartIndex(index => Math.max(0, index - 1));
  const onClickNextBtn = (): void => setChartIndex(index => Math.min(index + 1, actualPages.length - 1));

  const renderChartContent = (): React.ReactNode => {
    if (isLoading) {
      return <Skeleton active title={false} paragraph={{ rows: 7 }} style={{ width: '100%', height: '100%' }} />;
    }
    if (isEmpty) {
      return (
        <div style={{ padding: '30px 0' }}>
          <NoResultsEmpty />
        </div>
      );
    }
    return (
      <>
        {shouldRenderChart && !isNil(chartPlotHeight) && (
          <SitePerformanceChartJsChart
            chartPlotHeight={chartPlotHeight}
            pageActualData={pageActualData}
            pagePlannedData={pagePlannedData}
            isGcd={isGcd}
            startDate={startDate}
            endDate={endDate}
            chartIndex={chartIndex}
            data={sortedData}
          />
        )}
      </>
    );
  };

  return (
    <StyledSitePerformanceChartJsWrapper>
      <SitePerformanceChartJsGlobalStyles />

      <div
        className={classnames('site-performance-chart__pagination', {
          'site-performance-chart__pagination--disabled': !hasPagination || isFirstPage,
        })}
      >
        <div className="site-performance-chart__pagination-container">
          <KccButton
            type="tertiary"
            icon={<SvgIcon name="chevronUp" />}
            disabled={isFirstPage}
            onClick={onClickPrevBtn}
            className="site-performance-chart__pagination-button--previous"
          />
        </div>
      </div>

      <StyledSitePerformanceChartJs className="site-performance-chart" chartPlotHeight={chartPlotHeight}>
        <div className="site-performance-chart__title-bar">
          <h4 className="site-performance-chart__title">{t('overview.fleetDashboard.sitePerformanceChart.title')}</h4>
        </div>

        {/* TODO: enable later after new discussion */}
        {/* <SitePerformanceChartFilter /> */}

        <Flex className="site-performance-chart__sort-bar" align="center" gap={kccThemeConstants.SPACING.XXS}>
          <Typography.Text className="site-performance-chart__sort-label">
            {t('overview.fleetDashboard.sitePerformanceChart.sortBy')}
          </Typography.Text>
          <Segmented
            className="site-performance-chart__sort-segmented"
            value={sortOrder}
            onChange={(value): void => setSortOrder(value as SortOrder)}
            options={[
              { label: t('overview.fleetDashboard.sitePerformanceChart.sort.alphabetical'), value: 'alphabetical' },
              { label: t('overview.fleetDashboard.sitePerformanceChart.sort.descending'), value: 'descending' },
              { label: t('overview.fleetDashboard.sitePerformanceChart.sort.ascending'), value: 'ascending' },
            ]}
          />
        </Flex>

        <Flex className="site-performance-chart__legend" gap={kccThemeConstants.SPACING.SM} align="center" wrap>
          <Flex gap={kccThemeConstants.SPACING.XS} align="center">
            <div className="site-performance-chart__chart-note-square-left" />
            <Typography.Text className="site-performance-chart__chart-note-text">
              {t('operatingHoursComparisonChart.legend.actualOperatingHours')}
            </Typography.Text>
          </Flex>

          {isGcd && (
            <Flex gap={kccThemeConstants.SPACING.XS} align="center">
              <div className="site-performance-chart__chart-note-square-right" />
              <Typography.Text className="site-performance-chart__chart-note-text">
                {t('operatingHoursComparisonChart.legend.plannedOperatingHours')}
              </Typography.Text>
            </Flex>
          )}
        </Flex>

        {renderChartContent()}
      </StyledSitePerformanceChartJs>

      <div
        className={classnames('site-performance-chart__pagination', {
          'site-performance-chart__pagination--disabled': !hasPagination || isLastPage,
        })}
      >
        <div className="site-performance-chart__pagination-container">
          <KccButton
            type="tertiary"
            icon={<SvgIcon name="chevronDown" />}
            disabled={isLastPage}
            onClick={onClickNextBtn}
            className="site-performance-chart__pagination-button--next"
          />
        </div>
      </div>
    </StyledSitePerformanceChartJsWrapper>
  );
};
